import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Droplet, Plus, Trash2 } from "lucide-react";
import SafetyShell from "../safety/SafetyShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function PowderMasters({ user, onLogout }) {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shades, setShades] = useState([]);
  const [makes, setMakes] = useState([]);
  const [batches, setBatches] = useState([]);

  const [newShade, setNewShade] = useState("");
  const [newMake, setNewMake] = useState("");

  const [batchDialog, setBatchDialog] = useState(false);
  const [batchForm, setBatchForm] = useState({ batch_no: "", shade_id: "", make_id: "" });

  const [delDialog, setDelDialog] = useState({ open: false, type: null, item: null });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [permRes, sRes, mRes, bRes] = await Promise.all([
        axios.get(`${API}/auth/me/permissions`, auth()),
        axios.get(`${API}/powder/shades`, auth()),
        axios.get(`${API}/powder/makes`, auth()),
        axios.get(`${API}/powder/batches`, auth()),
      ]);
      setPermissions(permRes.data.effective_permissions || []);
      setShades(sRes.data);
      setMakes(mRes.data);
      setBatches(bRes.data);
    } catch (e) {
      toast.error("Failed to load powder masters");
    } finally {
      setLoading(false);
    }
  };

  const canManage = permissions.includes("powder.manage");

  const addShade = async () => {
    const name = newShade.trim(); if (!name) return;
    try {
      await axios.post(`${API}/powder/shades`, { name }, auth());
      setNewShade(""); toast.success("Shade added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addMake = async () => {
    const name = newMake.trim(); if (!name) return;
    try {
      await axios.post(`${API}/powder/makes`, { name }, auth());
      setNewMake(""); toast.success("Make added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addBatch = async () => {
    if (!batchForm.batch_no.trim() || !batchForm.shade_id || !batchForm.make_id) {
      toast.error("All fields required"); return;
    }
    try {
      await axios.post(`${API}/powder/batches`, batchForm, auth());
      setBatchDialog(false); setBatchForm({ batch_no: "", shade_id: "", make_id: "" });
      toast.success("Batch added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    const { type, item } = delDialog;
    const url = `${API}/powder/${type}/${item.id}`;
    try {
      await axios.delete(url, auth());
      toast.success("Deleted");
      setDelDialog({ open: false, type: null, item: null });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
    </div>;
  }

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Powder Masters" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Powder Masters", active: true, depth: 1 },
        { label: "Production Reports", to: "/safety/production-reports", depth: 1 },
      ]}
    >
      <div data-testid="powder-masters-page">
      <div className="sf-surface p-4 mb-4 flex items-center gap-3">
        <Droplet className="w-5 h-5 text-orange-600" />
        <div>
          <h1 className="text-xl font-bold tracking-tight">Powder Masters</h1>
          <p className="text-xs text-zinc-500">Manage powder shades, makes, and batches</p>
        </div>
      </div>

      <div>
        <Tabs defaultValue="batches" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="batches" data-testid="tab-batches">Batches ({batches.length})</TabsTrigger>
            <TabsTrigger value="shades" data-testid="tab-shades">Shades ({shades.length})</TabsTrigger>
            <TabsTrigger value="makes" data-testid="tab-makes">Makes ({makes.length})</TabsTrigger>
          </TabsList>

          {/* BATCHES */}
          <TabsContent value="batches">
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="font-semibold">Powder Batches</h2>
                {canManage && (
                  <Button onClick={() => setBatchDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-batch-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Batch
                  </Button>
                )}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left">Batch No</th>
                    <th className="p-3 text-left">Shade</th>
                    <th className="p-3 text-left">Make</th>
                    <th className="p-3 text-left">Created</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-gray-500">No batches yet.</td></tr>}
                  {batches.map(b => (
                    <tr key={b.id} className="border-b hover:bg-gray-50" data-testid={`batch-row-${b.id}`}>
                      <td className="p-3 font-medium">{b.batch_no}</td>
                      <td className="p-3">{b.shade_name}</td>
                      <td className="p-3">{b.make_name}</td>
                      <td className="p-3 text-gray-500">{new Date(b.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                      <td className="p-3">
                        {canManage && (
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDelDialog({ open: true, type: "batches", item: b })} data-testid={`delete-batch-${b.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* SHADES */}
          <TabsContent value="shades">
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b">
                {canManage && (
                  <div className="flex gap-2">
                    <Input placeholder="New shade name (e.g., Safety Yellow)" value={newShade} onChange={(e) => setNewShade(e.target.value)} data-testid="new-shade-input" />
                    <Button onClick={addShade} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-shade-btn"><Plus className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
              <ul className="divide-y">
                {shades.length === 0 && <li className="p-6 text-center text-gray-500">No shades yet.</li>}
                {shades.map(s => (
                  <li key={s.id} className="p-3 flex items-center justify-between" data-testid={`shade-row-${s.id}`}>
                    <span>{s.name}</span>
                    {canManage && (
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDelDialog({ open: true, type: "shades", item: s })} data-testid={`delete-shade-${s.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>

          {/* MAKES */}
          <TabsContent value="makes">
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b">
                {canManage && (
                  <div className="flex gap-2">
                    <Input placeholder="New make name (e.g., Nerolac)" value={newMake} onChange={(e) => setNewMake(e.target.value)} data-testid="new-make-input" />
                    <Button onClick={addMake} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-make-btn"><Plus className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
              <ul className="divide-y">
                {makes.length === 0 && <li className="p-6 text-center text-gray-500">No makes yet.</li>}
                {makes.map(m => (
                  <li key={m.id} className="p-3 flex items-center justify-between" data-testid={`make-row-${m.id}`}>
                    <span>{m.name}</span>
                    {canManage && (
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDelDialog({ open: true, type: "makes", item: m })} data-testid={`delete-make-${m.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Batch dialog */}
      <Dialog open={batchDialog} onOpenChange={setBatchDialog}>
        <DialogContent data-testid="batch-dialog">
          <DialogHeader><DialogTitle>Add Powder Batch</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Batch Number *</Label>
              <Input value={batchForm.batch_no} onChange={(e) => setBatchForm({ ...batchForm, batch_no: e.target.value })} placeholder="e.g., L100040435Y94" data-testid="batch-no-input" />
            </div>
            <div>
              <Label>Shade *</Label>
              <Select value={batchForm.shade_id} onValueChange={(v) => setBatchForm({ ...batchForm, shade_id: v })}>
                <SelectTrigger data-testid="batch-shade-select"><SelectValue placeholder="Select shade" /></SelectTrigger>
                <SelectContent>
                  {shades.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {shades.length === 0 && <p className="text-xs text-amber-600 mt-1">Add a shade from the Shades tab first.</p>}
            </div>
            <div>
              <Label>Make *</Label>
              <Select value={batchForm.make_id} onValueChange={(v) => setBatchForm({ ...batchForm, make_id: v })}>
                <SelectTrigger data-testid="batch-make-select"><SelectValue placeholder="Select make" /></SelectTrigger>
                <SelectContent>
                  {makes.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {makes.length === 0 && <p className="text-xs text-amber-600 mt-1">Add a make from the Makes tab first.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchDialog(false)}>Cancel</Button>
            <Button onClick={addBatch} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-batch-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={delDialog.open} onOpenChange={(o) => !o && setDelDialog({ open: false, type: null, item: null })}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader><DialogTitle>Delete?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">This will permanently remove <b>{delDialog.item?.name || delDialog.item?.batch_no}</b>.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelDialog({ open: false, type: null, item: null })}>Cancel</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </SafetyShell>
  );
}
